import { Box, Grid, Typography } from "@mui/material";
import { playerTeamLogo } from "../../../Assets";
import {
  formatToIndianCurrencyWords2,
  teamGradients,
} from "../../../Utils/helperFunctions";

const getTeamLogo = (team: any) => {
  return team?.Logo_URL__c || playerTeamLogo;
};

const AdminTeamSection: React.FC<any> = ({ teamDetails, onTeamClick }) => {
  return (
    <Box mt={{ xs: 1.6, md: 2.2 }}>
      <Grid
        container
        sx={{ width: "100%" }}
        columnSpacing={{ xs: 1.2, md: 2 }}
        rowSpacing={{ xs: 3.8, md: 5.2 }}
      >
        {teamDetails
          ?.slice()
          .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
          .map((team: any, i: number) => (
            <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
              <AdminTeamCard team={team} onClick={() => onTeamClick?.(team)} />
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

const AdminTeamCard: React.FC<any> = ({ team, onClick }) => {
  const players = team?.Player_Registrations__r?.records || [];
  const gradient = teamGradients[team?.Name] || { from: "#1e1e1e", to: "#444" };
  const getCategoryCount = (level: string) =>
    players.filter((p: any) => p.Recent_Competitive_Level__c === level).length;
  const totalPlayers = players?.length;

  const remainingFundFormatted = formatToIndianCurrencyWords2(
    team?.Fund_Remaining__c
  );

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: { xs: 1, md: 1.1 },
        px: { xs: 1.1, md: 1.3 },
        pb: { xs: 1, md: 1.2 },
        pt: { xs: 5.1, md: 6.2 },
        display: "flex",
        justifyContent: "center",
        background: `linear-gradient(to bottom, ${gradient.from}, ${gradient.to})`,
        minHeight: { xs: 198, md: 228 },
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          position: "absolute",
          zIndex: 3,
          textAlign: "center",
          top: { xs: -28, md: -30 },
          backgroundColor: "#fff",
          p: { xs: 0.3, md: 0.35 },
          width: { xs: 82, md: 94 },
          height: { xs: 82, md: 94 },
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: "999px",
          overflow: "hidden",
        }}
      >
        <img
          src={getTeamLogo(team)}
          alt="Team Logo"
          width={100}
          height={100}
          style={{
            height: "100%",
            width: "100%",
            margin: "0 auto",
            objectFit: "contain",
          }}
        />
      </Box>

      <Box
        sx={{
          textAlign: "center",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "0.6rem", md: "0.75rem" },
            fontWeight: 600,
            mb: 0.2,
            mt: { xs: 1.6, md: 4 },
          }}
          color="white"
        >
          Fund remaining
        </Typography>
        <Typography
          variant="h1"
          sx={{ fontSize: { xs: "1.65rem", md: "2rem" }, 
          mb: { xs: 1.6, md: 2 }, 
          fontWeight: 700, lineHeight: 1.08 }}
          color="white"
        >
          {remainingFundFormatted}
        </Typography>

        <Grid container spacing={{ xs: 0.55, md: 0.75 }} mt={0.2}>
          {[
            {
              label: "Icon",
              count: getCategoryCount("Indian senior team"),
              minPlayerCount: 1,
            },
            {
              label: "Senior",
              count: getCategoryCount(
                "First class, list A, BCCI Senior Men T20"
              ),
              minPlayerCount: 4,
            },
            {
              label: "Emerging",
              count: getCategoryCount(
                "Mumbai age group team (under 23 or under 19)"
              ),
              minPlayerCount: 6,
            },
            {
              label: "Development",
              count: getCategoryCount("Local club team"),
              minPlayerCount: 5,
            },
          ].map((item, index) => (
            <Grid item xs={6} key={index}>
              <Box
                sx={{
                  bgcolor: "#FFFFFF36",
                  py: { xs: 0.35, md: 0.45 },
                  width: "100%",
                  color: "#fff",
                  borderRadius: { xs: 0.6, md: 0.7 },
                  border: "0.5px solid #EAEAEA",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontSize: { xs: "1rem", md: "1.2rem" }, fontWeight: 700, mb: 0.1 }}
                  color="white"
                >
                  {item.count}/{item.minPlayerCount}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "0.58rem", md: "0.75rem" },
                    fontWeight: 600,
                    py: 0.1,
                    background:
                      "linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.30) 50%, rgba(255, 255, 255, 0.00) 100%)",
                  }}
                  color="white"
                >
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: "#FFFFFF36",
                py: { xs: 0.35, md: 0.45 },
                width: "100%",
                color: "#fff",
                borderRadius: { xs: 0.6, md: 0.7 },
                border: "0.5px solid #EAEAEA",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: "1rem", md: "1.2rem" }, fontWeight: 700, mb: 0.1 }}
                color="white"
              >
                {totalPlayers}/18
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "0.58rem", md: "0.75rem" },
                  fontWeight: 600,
                  py: 0.1,
                  background:
                    "linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.30) 50%, rgba(255, 255, 255, 0.00) 100%)",
                }}
                color="white"
              >
                Total Players
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminTeamSection;
